import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetTherapyByPatientQueryKey,
  useCreateTherapySupplementByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FileInput } from '../../../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { blobToBase64 } from '../../../../../utils/file.ts';

interface FormValues {
  date: string;
  file: File | null;
}

export function CreateSupplementDialog({
  open,
  onClose,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
}) {
  const queryClient = useQueryClient();

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      date: '',
      file: null,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const createSupplementMutation = useCreateTherapySupplementByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetTherapyByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Hinzufügen der Ergänzungsmeldung ist ein Fehler aufgetreten');
      },
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 600 }}>
        <ModalClose />
        <Typography level={'h4'}>Ergänzungsmeldung hinzufügen</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit(async (data) => {
            createSupplementMutation.mutate({
              id: patientId,
              data: {
                date: data.date,
                file: {
                  fileName: data.file!.name,
                  file: await blobToBase64(data.file!),
                },
              },
            });
          })}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.date}>
                <FormLabel>
                  <Typography>Datum *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="date"
                  rules={{
                    required: true,
                  }}
                />
                {errors.date && <FormHelperText>Datum muss angegeben werden</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.file} sx={{ gridColumn: '1 / span 2' }}>
                <FormLabel>
                  <Typography>Datei *</Typography>
                </FormLabel>
                <Controller
                  name={'file'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <FileInput
                      value={value}
                      onChange={onChange}
                      accept={'application/pdf'}
                      placeholder={'Datei hochladen'}
                    />
                  )}
                />
                {errors.file && <FormHelperText>Datei muss hochgeladen werden</FormHelperText>}
              </FormControl>
            </FormGrid>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createSupplementMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
